import { useEffect, useRef, useState } from 'react'

import { cn } from '@/components/ui/utils'
import type { Pt } from '@/geo/types'

import { MAGNIFIER_SIZE, MAGNIFIER_ZOOM } from './tools/magnifier'

/**
 * The loupe (§3.2 `Magnifier.tsx`): a 200×200 window showing the plate at
 * 4× around the cursor, so a vertex can be placed on a one-pixel edge
 * without zooming the whole canvas and losing context.
 *
 * The plate is drawn once into an offscreen `<canvas>` when the image loads
 * and then blitted per frame — cheap enough to run on every pointer move.
 * The crosshair is drawn in the loupe rather than in the page so it lands on
 * the magnified pixel, not near it.
 */

export interface MagnifierProps {
  /** Same-origin plate URL — the one the canvas is showing. */
  src?: string
  /** Cursor position in percent, or `null` to hide. */
  at: Pt | null
  /** Where to park the loupe: away from the cursor's own quadrant. */
  className?: string
  size?: number
  zoom?: number
}

export function Magnifier({ src, at, className, size = MAGNIFIER_SIZE, zoom = MAGNIFIER_ZOOM }: MagnifierProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [image, setImage] = useState<HTMLImageElement | null>(null)

  useEffect(() => {
    if (!src) {
      setImage(null)
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'
    let cancelled = false
    img.onload = () => {
      if (!cancelled) setImage(img)
    }
    img.src = src
    return () => {
      cancelled = true
    }
  }, [src])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !at) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    if (canvas.width !== size * dpr) {
      canvas.width = size * dpr
      canvas.height = size * dpr
    }
    ctx.save()
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = 'rgba(0,0,0,0.35)'
    ctx.fillRect(0, 0, size, size)

    if (image && image.naturalWidth > 0) {
      const sx = (at[0] / 100) * image.naturalWidth
      const sy = (at[1] / 100) * image.naturalHeight
      const sw = size / zoom
      const sh = size / zoom
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(image, sx - sw / 2, sy - sh / 2, sw, sh, 0, 0, size, size)
    }

    // Crosshair
    ctx.strokeStyle = 'rgba(255,255,255,0.9)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(size / 2, size / 2 - 10)
    ctx.lineTo(size / 2, size / 2 + 10)
    ctx.moveTo(size / 2 - 10, size / 2)
    ctx.lineTo(size / 2 + 10, size / 2)
    ctx.stroke()
    ctx.restore()
  }, [at, image, size, zoom])

  if (!at) return null

  // Park it in the corner the cursor is furthest from.
  const corner = at[0] < 50 ? (at[1] < 50 ? 'bottom-3 right-3' : 'top-3 right-3') : at[1] < 50 ? 'bottom-3 left-3' : 'top-3 left-3'

  return (
    <div
      className={cn('pointer-events-none absolute z-20 overflow-hidden rounded-xl border-2 border-line-strong shadow-card', corner, className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <canvas ref={canvasRef} style={{ width: size, height: size, display: 'block' }} />
    </div>
  )
}
